import React from "react";
import { FormProvider } from "react-hook-form";
import { useParams } from "react-router";
import { Button, CircularProgress, Form, RequiredFieldsLabel } from "@sscs/ui";
import { LoginContentWrapper } from "Components";
import loginLayoutStyles from "../../../../components/LoginLayout/loginLayout.module.scss";
import { useChangePasswordForm } from "../../hooks";
import { useVerifyPasswordResetQuery } from "../../services/passwordReset.api";
import ConfirmChangePassword from "../ConfirmChangePassword";

const ChangePasswordForm = (): React.JSX.Element => {
  const { verificationToken } = useParams();

  // validate the reset token and render form if it passes
  const { isLoading, isFetching, isError } = useVerifyPasswordResetQuery({
    verificationToken: verificationToken ?? "",
  });

  const {
    errors,
    methods,
    onSubmit,
    isPasswordChangeSuccessful,
    isPasswordChangeError,
  } = useChangePasswordForm(verificationToken);

  if (isPasswordChangeSuccessful) return <ConfirmChangePassword />;
  if (isLoading || isFetching)
    return (
      <LoginContentWrapper
        title="Change your password"
        byline="Set your password and confirm it below"
      >
        <CircularProgress />
      </LoginContentWrapper>
    );
  if (isError || !verificationToken)
    return (
      <LoginContentWrapper
        title="Invalid or expired token"
        byline='Try requesting a new token with the "Forgot my password" link on the login page'
      />
    );
  if (isPasswordChangeError)
    return (
      <LoginContentWrapper
        title="Could not set new password"
        byline='Try requesting a new password reset email with the "Forgot my password" link on the login page'
      />
    );

  return (
    <LoginContentWrapper
      title="Change your password"
      byline="Set your password and confirm it below"
    >
      <FormProvider {...methods}>
        <Form.Form onSubmit={onSubmit}>
          <Form.TextField
            id="change-password-new"
            type="password"
            label="New password"
            name="newPassword"
            autoComplete="new-password"
            isRequired
            errors={errors}
          />
          <Form.TextField
            id="change-password-confirm"
            type="password"
            label="Confirm new password"
            name="confirmPassword"
            autoComplete="new-password"
            isRequired
            errors={errors}
          />
          <RequiredFieldsLabel />
          <div className={loginLayoutStyles["form-button"]}>
            <Button isFullWidth type="submit" color="primary">
              Change password
            </Button>
          </div>
        </Form.Form>
      </FormProvider>
    </LoginContentWrapper>
  );
};

export default ChangePasswordForm;
